/**
 * Merchants ROI Calculator 组件
 * 处理文本输入框、滑块和实时计算
 */
export function initMerchantsROICalculator() {
  // 获取所有输入元素
  const visitorsInput = document.getElementById('roi-visitors');
  const priceInput = document.getElementById('roi-price');
  const sessionInput = document.getElementById('roi-session');
  const couponInput = document.getElementById('roi-coupon');
  const upsellInput = document.getElementById('roi-upsell');
  const powerInput = document.getElementById('roi-power');
  const batterySlider = document.getElementById('roi-battery');
  
  // 获取结果元素
  const calculateBtn = document.getElementById('roi-calculate-btn');
  const resultsContainer = document.getElementById('roi-results');
  const dailyRevenueDisplay = document.getElementById('roi-daily-revenue');
  const monthlyRevenueDisplay = document.getElementById('roi-monthly-revenue');
  const paybackPeriodDisplay = document.getElementById('roi-payback-period');
  const sensitivityDisplay = document.getElementById('roi-sensitivity');
  const batteryLabel = document.getElementById('roi-battery-label');

  if (!visitorsInput || !priceInput || !sessionInput || !couponInput || 
      !upsellInput || !powerInput || !batterySlider || !calculateBtn ||
      !resultsContainer || !dailyRevenueDisplay || !monthlyRevenueDisplay ||
      !paybackPeriodDisplay || !sensitivityDisplay || !batteryLabel) {
    console.warn('Merchants ROI Calculator: Required elements not found');
    return null;
  }

  // 设备成本（用于计算回本周期，可以根据实际情况调整）
  const DEVICE_COST = 1000; // 假设设备成本为 $1000

  // 初始值配置
  const initialValues = {
    visitors: 0,
    price: 3.5,
    session: 1,
    coupon: 0,
    upsell: 0,
    power: 0,
    battery: 0,
  };

  // 设置初始值
  visitorsInput.value = initialValues.visitors;
  priceInput.value = initialValues.price;
  sessionInput.value = initialValues.session;
  couponInput.value = initialValues.coupon;
  upsellInput.value = initialValues.upsell;
  powerInput.value = initialValues.power;
  batterySlider.value = initialValues.battery;

  // 格式化货币
  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(num);
  };

  // 更新滑块填充进度和标签
  const updateSliderFill = (slider) => {
    const min = Number(slider.min || 0);
    const max = Number(slider.max || 100);
    const value = Number(slider.value || min);
    const percent = max === min ? 0 : ((value - min) / (max - min)) * 100;
    slider.style.setProperty('--roi-progress', `${percent}%`);
    
    // 更新标签显示
    if (batteryLabel && slider.id === 'roi-battery') {
      batteryLabel.textContent = value.toString();
    }
  };

  // 计算每日收入
  const calculateDailyRevenue = () => {
    const visitors = Number(visitorsInput.value || 0);
    const price = Number(priceInput.value || 0);
    const session = Number(sessionInput.value || 0);
    const coupon = Number(couponInput.value || 0) / 100; // 转换为小数
    const upsell = Number(upsellInput.value || 0) / 100; // 转换为小数
    const power = Number(powerInput.value || 0);
    const battery = Number(batterySlider.value || 0) / 100; // 转换为小数

    // 基础计算：每日访问者 × 价格 × 平均会话时长
    let baseRevenue = visitors * price * session;

    // 应用优惠券折扣
    baseRevenue = baseRevenue * (1 - coupon);

    // 应用追加销售转换率
    const upsellRevenue = baseRevenue * upsell;
    baseRevenue = baseRevenue + upsellRevenue;

    // 减去电力成本（假设每个会话的电力成本）
    const powerCost = visitors * session * power;
    baseRevenue = baseRevenue - powerCost;

    // 应用低电量率（低电量时可能无法提供服务）
    baseRevenue = baseRevenue * (1 - battery);

    return Math.max(0, baseRevenue); // 确保不为负数
  };

  // 计算月度收入
  const calculateMonthlyRevenue = () => {
    const dailyRevenue = calculateDailyRevenue();
    return dailyRevenue * 30; // 假设每月30天
  };

  // 计算回本周期（月）
  const calculatePaybackPeriod = () => {
    const monthlyRevenue = calculateMonthlyRevenue();
    if (monthlyRevenue <= 0) return 0;
    return Math.ceil(DEVICE_COST / monthlyRevenue);
  };

  // 计算敏感度范围（±10%）
  const calculateSensitivity = () => {
    const monthlyRevenue = calculateMonthlyRevenue();
    const sensitivity = monthlyRevenue * 0.1; // 10% 的敏感度
    return Math.round(sensitivity);
  };

  // 显示计算结果
  const showResults = () => {
    const dailyRevenue = calculateDailyRevenue();
    const monthlyRevenue = calculateMonthlyRevenue();
    const paybackPeriod = calculatePaybackPeriod();
    const sensitivity = calculateSensitivity();

    dailyRevenueDisplay.textContent = formatCurrency(dailyRevenue);
    monthlyRevenueDisplay.textContent = formatCurrency(monthlyRevenue);
    paybackPeriodDisplay.textContent = paybackPeriod.toString();
    sensitivityDisplay.textContent = formatCurrency(sensitivity);

    // 显示结果区域
    resultsContainer.style.display = 'flex';
    
    // 滚动到结果区域
    resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  // 处理输入框值变化
  const handleInputChange = (input, min, max, step) => {
    let value = Number(input.value || 0);
    
    // 确保值在范围内
    if (value < min) value = min;
    if (value > max) value = max;
    
    // 根据 step 调整值
    if (step) {
      value = Math.round(value / step) * step;
    }
    
    input.value = value;
  };

  // 处理上下箭头按钮
  const handleArrowButton = (input, direction) => {
    const min = Number(input.min || 0);
    const max = Number(input.max || 100);
    const step = Number(input.step || 1);
    let value = Number(input.value || min);

    if (direction === 'up') {
      value = Math.min(value + step, max);
    } else {
      value = Math.max(value - step, min);
    }

    input.value = value;
  };

  // 绑定按钮点击事件
  calculateBtn.addEventListener('click', showResults);

  // 绑定输入事件
  const inputs = [visitorsInput, priceInput, sessionInput, couponInput, upsellInput, powerInput];
  inputs.forEach((input) => {
    input.addEventListener('input', () => {
      const min = Number(input.min || 0);
      const max = Number(input.max || 100);
      const step = Number(input.step || 1);
      handleInputChange(input, min, max, step);
    });

    // 处理失焦事件，确保值有效
    input.addEventListener('blur', () => {
      const min = Number(input.min || 0);
      const max = Number(input.max || 100);
      const step = Number(input.step || 1);
      handleInputChange(input, min, max, step);
    });
  });

  // 绑定滑块事件
  batterySlider.addEventListener('input', () => {
    updateSliderFill(batterySlider);
  });

  // 绑定上下箭头按钮事件
  const arrowButtons = document.querySelectorAll('.roi-calculator__input-btn');
  arrowButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const fieldId = button.getAttribute('data-field');
      const input = document.getElementById(fieldId);
      if (!input) return;

      const direction = button.classList.contains('roi-calculator__input-btn--up') ? 'up' : 'down';
      handleArrowButton(input, direction);
    });
  });

  // 初始化
  updateSliderFill(batterySlider);

  // 返回控制对象
  return {
    calculate: showResults,
    getValues: () => ({
      visitors: Number(visitorsInput.value || 0),
      price: Number(priceInput.value || 0),
      session: Number(sessionInput.value || 0),
      coupon: Number(couponInput.value || 0),
      upsell: Number(upsellInput.value || 0),
      power: Number(powerInput.value || 0),
      battery: Number(batterySlider.value || 0),
      dailyRevenue: calculateDailyRevenue(),
      monthlyRevenue: calculateMonthlyRevenue(),
      paybackPeriod: calculatePaybackPeriod(),
      sensitivity: calculateSensitivity(),
    }),
    setValues: (values) => {
      if (values.visitors !== undefined) visitorsInput.value = values.visitors;
      if (values.price !== undefined) priceInput.value = values.price;
      if (values.session !== undefined) sessionInput.value = values.session;
      if (values.coupon !== undefined) couponInput.value = values.coupon;
      if (values.upsell !== undefined) upsellInput.value = values.upsell;
      if (values.power !== undefined) powerInput.value = values.power;
      if (values.battery !== undefined) batterySlider.value = values.battery;
      updateSliderFill(batterySlider);
    },
    reset: () => {
      visitorsInput.value = initialValues.visitors;
      priceInput.value = initialValues.price;
      sessionInput.value = initialValues.session;
      couponInput.value = initialValues.coupon;
      upsellInput.value = initialValues.upsell;
      powerInput.value = initialValues.power;
      batterySlider.value = initialValues.battery;
      updateSliderFill(batterySlider);
      resultsContainer.style.display = 'none';
    },
  };
}

